import { useAuth } from "@/platforms/customer/context/AuthContext";
import { getProfileSummary } from "@/platforms/customer/services/profileService";
import { useAppLocation } from "@/lib/app-navigation";
import { useRouter, usePathname } from "next/navigation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { getPublishedProducts } from "@/platforms/customer/services/productService";
import { getBlogs } from "@/platforms/customer/services/blogService";
import { useSearchData } from "@/platforms/customer/hooks/useSearchData";
import { navigateToTourProduct } from "@/utils/tourUrl";

// header logic
export const useHeader = (heroRef) => {
  const router = useRouter();
  const pathname = usePathname();
  const { isAuthenticated } = useAuth();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const profileRef = useRef(null);
  const location = useAppLocation();
  const isLandingPage = pathname === "/";
  const [showSearch, setShowSearch] = useState(!isLandingPage);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [localProducts, setLocalProducts] = useState([]);
  const [localBlogs, setLocalBlogs] = useState([]);
  const [query, setQuery] = useState("");
  const isHomePage = pathname === "/";
  // Redux store se items nikalna
  const cartItems = useSelector((state) => state.cart.items);
  const wishlistItems = useSelector((state) => state.wishlist.items);

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Total quantity calculate karna (Now counting unique products, not persons)
  const totalQuantity = isMounted ? cartItems.length : 0;
  const wishlistCount = isMounted ? wishlistItems.length : 0;

  const { getFilteredSuggestions, isReviewPage } = useSearchData(
    localProducts,
    localBlogs,
  );

  const suggestions = getFilteredSuggestions(query);

  // Ticket 2: do NOT fetch published products on header mount / homepage paint.
  // Desktop SearchBar already searches via its own typing request; BlogHeader
  // suggestions need this list only after the search UI is opened.
  const searchDataLoadedRef = useRef(false);
  const searchDataInflightRef = useRef(null);

  useEffect(() => {
    if (!isSearchOpen) return;
    if (searchDataLoadedRef.current) return;
    if (searchDataInflightRef.current) return;

    const loadSearchData = async () => {
      try {
        const [pData, bData] = await Promise.all([
          getPublishedProducts({ page: 1, page_size: 50 }),
          getBlogs({ summary: 1 }),
        ]);
        setLocalProducts(pData?.products || []);
        setLocalBlogs(bData?.blogs || []);
        searchDataLoadedRef.current = true;
      } catch (err) {
        console.error("Error loading search data", err);
      } finally {
        searchDataInflightRef.current = null;
      }
    };

    searchDataInflightRef.current = loadSearchData();
  }, [isSearchOpen]);

  // route change par reset
  useEffect(() => {
    setShowSearch(!isLandingPage);
  }, [isLandingPage]);

  // hero scroll logic (ONLY landing page)
  useEffect(() => {
    if (!isLandingPage) return;

    let raf = 0;
    const update = () => {
      const heroEl = heroRef?.current;
      if (!heroEl) return;

      const rect = heroEl.getBoundingClientRect();
      const heroHeight = rect.height || heroEl.offsetHeight || 0;
      const half = heroHeight * 0.4;
      // When the user has scrolled enough that >= half the hero is above the viewport top,
      // the hero's top will be <= -half.
      const scrolledPastHalf = rect.top <= -half;
      setShowSearch(scrolledPastHalf);
    };

    const onScrollOrResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    // Initial measurement after paint
    raf = requestAnimationFrame(update);

    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isLandingPage, heroRef, location.pathname]);

  // Get userId only if authenticated - don't use default
  const userId = isAuthenticated
    ? localStorage.getItem("customer_profile_user_id") || null
    : null;

  // Load cached profile summary immediately on mount (synchronous) - only if authenticated
  const [profileSummary, setProfileSummary] = useState(() => {
    if (!isAuthenticated || !userId) return null;
    try {
      const cached = localStorage.getItem(
        `customer_profile_summary_cache_${userId}`,
      );
      const timestamp = localStorage.getItem(
        `customer_profile_summary_cache_ts_${userId}`,
      );
      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp, 10);
        if (age < 10 * 60 * 1000) {
          // 10 minutes for summary
          return JSON.parse(cached);
        }
      }
    } catch (_) {
      // Ignore cache errors
    }
    return null;
  });
  const [profileLoading, setProfileLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      setProfileSummary(null);
      return;
    }
    // Only show loading if we don't have cached data
    if (!profileSummary) {
      setProfileLoading(true);
    }
    try {
      // Use lightweight summary endpoint for header
      const data = await getProfileSummary(userId, true); // Use cache
      setProfileSummary(data);
    } catch (_) {
      // Only clear if we don't have cached data
      if (!profileSummary) {
        setProfileSummary(null);
      }
    } finally {
      setProfileLoading(false);
    }
  }, [userId, profileSummary, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && userId) {
      // If we have cached data, fetch fresh in background
      if (profileSummary) {
        fetchProfile(); // This will fetch in background
      } else {
        // No cache, fetch immediately
        fetchProfile();
      }
    } else {
      setProfileSummary(null);
    }
  }, [isAuthenticated, userId]); // Run when authentication state or userId changes

  useEffect(() => {
    if (isAuthenticated && userId) {
      const handleProfileUpdated = () => fetchProfile();
      window.addEventListener("customer_profile_updated", handleProfileUpdated);
      return () =>
        window.removeEventListener(
          "customer_profile_updated",
          handleProfileUpdated,
        );
    }
  }, [fetchProfile, isAuthenticated, userId]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSelection = (item) => {
    if (isReviewPage) {
      // Agar review page par hain to sirf event fire karen, navigate nahi
      window.dispatchEvent(
        new CustomEvent("productSelected", { detail: item.data }),
      );
    } else {
      // Normal navigation
      if (item.type === "blog") {
        router.push(`/blog/${item.id}`);
      } else {
        navigateToTourProduct(
          router,
          item.data || {
            id: item.id,
            slug: item.slug,
            name: item.title,
          },
        );
      }
    }
    setIsSearchOpen(false);
    setQuery("");
  };

  const user = profileSummary?.user;

  const displayName = useMemo(() => {
    if (!user) return "";
    if (user.first_name || user.last_name) {
      return [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
    }
    return user.name || "";
  }, [user]);

  const initials = useMemo(() => {
    if (displayName) {
      const parts = displayName.split(/\s+/).filter(Boolean);
      if (parts.length === 1) return parts[0][0]?.toUpperCase() || "TA";
      return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
    }
    const fallback = user?.email || "TA";
    return fallback.slice(0, 2).toUpperCase();
  }, [displayName, user?.email]);

  const avatarUrl = user?.avatar_url;

  const headerPosition = isLandingPage
    ? "fixed top-0 left-0 right-0 bg-white"
    : "fixed top-0 left-0 right-0 bg-white";

  return {
    router,
    isAuthenticated,
    isSettingsOpen,
    setIsSettingsOpen,
    profileRef,
    showSearch,
    profileSummary,
    profileLoading,
    displayName,
    initials,
    avatarUrl,
    headerPosition,
    isScrolled,
    isMobileNavOpen,
    setIsMobileNavOpen,
    cartItems,
    wishlistItems,
    totalQuantity,
    wishlistCount,
    isSearchOpen,
    setIsSearchOpen,
    query,
    setQuery,
    suggestions,
    isReviewPage,
    handleSelection,
    isHomePage
  };
};

// footer logic
export const useFooter = () => {
  const router = useRouter();
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const footerRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Desktop-only: show button when footer comes into view.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth < 1024) return;
    const el = footerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowBackToTop(entry.isIntersecting),
      { root: null, threshold: 0.12 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSelectNavigate = (e) => {
    const path = e.target.value;
    if (path) {
      router.push(path);
      window.scrollTo(0, 0);
      e.target.value = "";
    }
  };

  return { scrollToTop, footerRef, showBackToTop, handleSelectNavigate };
};

// bottom navigation logic
export const useBottomNavigation = () => {
  const router = useRouter();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const attractionsRef = useRef(null);
  const destinationsRef = useRef(null);
  const exploreRef = useRef(null);

  // Redux store se items nikalna
  const cartItems = useSelector((state) => state.cart.items);
  const wishlistItems = useSelector((state) => state.wishlist.items);

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Total quantity calculate karna
  const totalQuantity = isMounted ? cartItems.length : 0;

  const wishlistCount = isMounted ? wishlistItems.length : 0;

  // Close dropdown when clicking outside (trigger or dropdown panel)
  useEffect(() => {
    if (!activeDropdown) return;
    const handleClickOutside = (e) => {
      const target = e.target;
      const isTrigger =
        attractionsRef.current?.contains(target) ||
        destinationsRef.current?.contains(target) ||
        exploreRef.current?.contains(target);
      const isPanel = target.closest("[data-dropdown-panel]");
      if (!isTrigger && !isPanel) setActiveDropdown(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  return {
    router,
    activeDropdown,
    setActiveDropdown,
    attractionsRef,
    destinationsRef,
    exploreRef,
    totalQuantity,
    wishlistCount,
  };
};

// Language Dropdown
export const useLanguageDropdown = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [language, setLanguage] = useState("en");
  const [currency, setCurrency] = useState("USD");
  const isOpen = Boolean(anchorEl);

  // Restore selected language & currency from localStorage on mount
  useEffect(() => {
    const savedLang = localStorage.getItem("selected_language");
    const savedCurr = localStorage.getItem("selected_currency");

    if (savedLang) setLanguage(savedLang);
    if (savedCurr) setCurrency(savedCurr.toUpperCase());
  }, []);

  // Save selection to localStorage
  const handleLanguageSelect = (code) => {
    setLanguage(code);
    localStorage.setItem("selected_language", code);
    setAnchorEl(null);
  };

  const handleCurrencySelect = (code) => {
    setCurrency(code.toUpperCase());
    localStorage.setItem("selected_currency", code);
    setAnchorEl(null);
  };

  return {
    anchorEl,
    setAnchorEl,
    activeTab,
    setActiveTab,
    language,
    currency,
    isOpen,
    handleLanguageSelect,
    handleCurrencySelect,
    
  };
};
